import React, { useState, useContext } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Package,
  Search,
  History,
  RotateCcw,
  AlertTriangle,
  X,
  ChevronRight,
  Lock,
  Info
} from 'lucide-react';
import { FBAInboundContext, STEPS } from './FBAInboundContext';

// Button Component
const Button = ({
  children,
  variant = 'primary',
  className = '',
  onClick,
  disabled = false,
  ...props
}) => {
  const baseStyles = 'inline-flex items-center justify-center font-semibold transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed px-4 py-2 text-sm';

  const variants = {
    primary: 'bg-blue-600 hover:bg-blue-700 text-white focus:ring-blue-500 border border-blue-600',
    secondary: 'bg-gray-100 hover:bg-gray-200 text-gray-900 focus:ring-gray-500 border border-gray-300',
    outline: 'border border-gray-900 bg-white hover:bg-gray-900 hover:text-white text-gray-900 focus:ring-gray-500',
    ghost: 'hover:bg-gray-100 text-gray-700 focus:ring-gray-500 border border-transparent',
    danger: 'bg-red-600 hover:bg-red-700 text-white focus:ring-red-500 border border-red-600'
  };

  return (
    <button
      className={`${baseStyles} ${variants[variant]} ${className}`}
      onClick={onClick}
      disabled={disabled}
      {...props}
    >
      {children}
    </button>
  );
};

function FBAInboundSidebar({ children }) {
  const location = useLocation();
  const context = useContext(FBAInboundContext);

  const [showResetModal, setShowResetModal] = useState(false);

  // Handle context not available (when not wrapped in provider)
  const {
    currentStep = 0,
    completedSteps = new Set(),
    maxAccessibleStep = 0,
    outboundShipment,
    planId,
    shipmentLocked,
    selectedPlacementOption,
    isStepAccessible,
    resetWorkflow,
    rollbackPlan,
    goToStep,
    loading
  } = context || {};

  // Determine why a step is locked based on plan progress
  const getStepLockReason = (stepId) => {
    if (stepId === 0) return null; // Step 0 is never locked

    if (shipmentLocked && stepId < 5) {
      return 'Shipment is locked - cannot modify earlier steps';
    }
    if (selectedPlacementOption && stepId < 4) {
      return 'Placement selected - cannot modify packing or address';
    }
    if (planId && stepId < 2) {
      return 'Plan created - address is locked into this plan';
    }
    return null;
  };

  // Check if step is locked (completed but no longer accessible)
  const isStepLocked = (stepId) => {
    return completedSteps.has(stepId) && !isStepAccessible?.(stepId);
  };

  const navItems = [
    { to: "/fba-inbound", label: "New Workflow", icon: Package },
    { to: "/fba-inbound/search", label: "Search Plans", icon: Search },
    { to: "/fba-inbound/history", label: "History", icon: History },
  ];

  const handleReset = async () => {
    if (planId && !shipmentLocked) {
      try {
        await rollbackPlan();
      } catch (err) {
        console.error('Rollback failed:', err);
      }
    } else {
      resetWorkflow();
    }
    setShowResetModal(false);
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <div className="w-72 bg-white border-r border-gray-200 flex flex-col">
        {/* Header */}
        <div className="h-16 flex items-center px-4 border-b border-gray-200 bg-blue-600">
          <Package className="h-5 w-5 text-white mr-2" />
          <h1 className="text-sm font-semibold text-white">FBA Inbound v2024</h1>
        </div>

        {/* Navigation */}
        <nav className="py-2 border-b border-gray-200">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.to;

            return (
              <RouterLink
                key={item.to}
                to={item.to}
                className={`flex items-center px-4 py-2.5 mx-2 my-0.5 text-sm font-medium transition-all duration-150 border-l-4 ${
                  isActive
                    ? 'bg-blue-50 text-blue-700 border-l-blue-600'
                    : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900 border-l-transparent hover:border-l-gray-300'
                }`}
              >
                <Icon className={`h-4 w-4 mr-3 flex-shrink-0 ${
                  isActive ? 'text-blue-600' : 'text-gray-500'
                }`} />
                <span className="truncate">{item.label}</span>
              </RouterLink>
            );
          })}
        </nav>

        {/* Current Shipment Info */}
        {outboundShipment && (
          <div className="p-4 border-b border-gray-200">
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
              Current Shipment
            </div>
            <div className="text-sm font-semibold text-gray-900 truncate">
              {outboundShipment.name || `Outbound #${outboundShipment.id}`}
            </div>
            {planId && (
              <div className="text-xs text-gray-500 mt-1">
                Plan ID: {planId}
              </div>
            )}
            {shipmentLocked && (
              <div className="flex items-center mt-2 text-xs text-amber-600">
                <AlertTriangle className="h-3 w-3 mr-1" />
                Shipment Locked
              </div>
            )}
          </div>
        )}

        {/* Step Progress */}
        <div className="flex-1 overflow-y-auto overflow-x-visible p-4">
          <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
            Workflow Progress
          </div>

          {/* Locked steps info banner */}
          {planId && (
            <div className="mb-3 p-2 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start text-xs text-amber-800">
                <Lock className="h-3 w-3 mr-1.5 mt-0.5 flex-shrink-0" />
                <div>
                  <span className="font-medium">Some steps are locked</span>
                  <p className="text-amber-600 mt-0.5">
                    {shipmentLocked
                      ? 'Shipment is confirmed with Amazon.'
                      : selectedPlacementOption
                        ? 'Placement is selected.'
                        : 'Plan is created.'}
                    {' '}Earlier steps cannot be modified.
                  </p>
                </div>
              </div>
            </div>
          )}
          <div className="space-y-1">
            {STEPS.map((step) => {
              const isCompleted = completedSteps.has(step.id);
              const isCurrent = currentStep === step.id;
              const stepAccessible = isStepAccessible ? isStepAccessible(step.id) : step.id <= maxAccessibleStep;
              const locked = isStepLocked(step.id);
              const lockReason = getStepLockReason(step.id);
              const canClick = stepAccessible && goToStep && !isCurrent && !locked;

              return (
                <div key={step.id} className="relative group">
                  <button
                    onClick={() => canClick && goToStep(step.id)}
                    disabled={!canClick}
                    className={`w-full flex items-center px-3 py-2 rounded text-sm text-left transition-colors ${
                      isCurrent
                        ? 'bg-blue-50 border border-blue-200'
                        : locked
                          ? 'bg-amber-50 border border-amber-200 cursor-not-allowed'
                          : isCompleted
                            ? 'bg-green-50 hover:bg-green-100'
                            : stepAccessible
                              ? 'bg-gray-50 hover:bg-gray-100'
                              : 'bg-gray-50 opacity-50 cursor-not-allowed'
                    } ${canClick ? 'cursor-pointer' : ''}`}
                  >
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center mr-3 text-xs font-medium ${
                      locked
                        ? 'bg-amber-500 text-white'
                        : isCompleted
                          ? 'bg-green-500 text-white'
                          : isCurrent
                            ? 'bg-blue-500 text-white'
                            : 'bg-gray-300 text-gray-600'
                    }`}>
                      {locked ? <Lock className="h-3 w-3" /> : isCompleted ? '✓' : step.id + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className={`font-medium truncate ${
                        locked
                          ? 'text-amber-700'
                          : isCurrent
                            ? 'text-blue-700'
                            : isCompleted
                              ? 'text-green-700'
                              : 'text-gray-600'
                      }`}>
                        {step.title}
                      </div>
                      <div className="text-xs text-gray-500 truncate">
                        {locked ? 'Locked' : step.description}
                      </div>
                    </div>
                    {isCurrent && (
                      <ChevronRight className="h-4 w-4 text-blue-500 flex-shrink-0" />
                    )}
                    {locked && (
                      <Lock className="h-4 w-4 text-amber-500 flex-shrink-0" />
                    )}
                  </button>
                  {/* Tooltip for locked steps - appears below */}
                  {locked && lockReason && (
                    <div className="absolute left-0 right-0 top-full mt-1 z-50 hidden group-hover:block">
                      <div className="bg-gray-900 text-white text-xs rounded-lg py-2 px-3 shadow-lg">
                        <div className="flex items-start">
                          <Info className="h-3 w-3 mr-1.5 mt-0.5 flex-shrink-0" />
                          <div>
                            <div>{lockReason}</div>
                            <div className="text-gray-400 mt-1">
                              Use "Reset Workflow" to start over
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="p-4 border-t border-gray-200 space-y-2">
          {planId && !shipmentLocked && (
            <Button
              onClick={() => setShowResetModal(true)}
              variant="danger"
              className="w-full justify-center py-2 text-sm"
              disabled={loading}
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset Workflow
            </Button>
          )}
          {!planId && currentStep > 0 && (
            <Button
              onClick={resetWorkflow}
              variant="outline"
              className="w-full justify-center py-2 text-sm"
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Start Over
            </Button>
          )}
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 overflow-auto bg-gray-50">
        {children}
      </div>

      {/* Reset Confirmation Modal */}
      {showResetModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="w-full max-w-md bg-white shadow-lg border border-gray-200 rounded-lg">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className="flex items-center text-red-600">
                <AlertTriangle className="h-5 w-5 mr-2" />
                <h3 className="text-lg font-semibold">Reset Workflow</h3>
              </div>
              <button
                onClick={() => setShowResetModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4">
              <p className="text-gray-700 mb-4">
                Are you sure you want to reset this workflow? This will:
              </p>
              <ul className="list-disc list-inside text-sm text-gray-600 space-y-1 mb-4">
                {planId && <li>Delete the current FBA plan (ID: {planId})</li>}
                <li>Clear all box groups and packing information</li>
                <li>Reset all progress back to Step 1</li>
              </ul>
              <p className="text-sm text-amber-600 font-medium">
                This action cannot be undone.
              </p>
            </div>

            <div className="flex justify-end space-x-2 p-4 border-t border-gray-200 bg-gray-50">
              <Button
                variant="outline"
                onClick={() => setShowResetModal(false)}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={handleReset}
                disabled={loading}
              >
                {loading ? 'Resetting...' : 'Reset Workflow'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FBAInboundSidebar;
